<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

interface TournamentRoster {
  id: number;
  hero_name?: string;
  faction_name: string;
  structure?: any;
  tournament_roster_url?: string;
}

interface Props {
  roster: TournamentRoster;
  rosterIndex: number;
  isActive: boolean;
  isClickable: boolean;
  showIndex?: boolean;
  compact?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  showIndex: true,
  compact: false
});

const { t } = useI18n();

const emit = defineEmits<{
  'roster-click': [rosterIndex: number];
  'roster-view': [roster: TournamentRoster];
  'roster-link': [roster: TournamentRoster];
}>();

const cardClasses = computed(() => ({
  'roster-card': true,
  'active': props.isActive,
  'clickable': props.isClickable && !props.isActive,
  'inactive': !props.isActive,
  'disabled': !props.isClickable,
  'compact': props.compact
}));

const handleCardClick = (event: Event) => {
  if (props.isClickable && !props.isActive) {
    event.preventDefault();
    event.stopPropagation();
    emit('roster-click', props.rosterIndex);
  }
};

const showRosterDetails = (event: Event) => {
  event.stopPropagation();
  emit('roster-view', props.roster);
};

const openRosterLink = (event: Event) => {
  event.preventDefault();
  event.stopPropagation();

  // Open the roster URL in a new tab
  if (props.roster.tournament_roster_url) {
    window.open(`/tournament-roster/${props.roster.tournament_roster_url}`, '_blank');
  }

  emit('roster-link', props.roster);
};
</script>

<template>
  <div :class="cardClasses" @click="handleCardClick">
    <div class="roster-header">
      <div v-if="showIndex && !compact" class="roster-index">{{ rosterIndex + 1 }}</div>
      <div class="roster-info">
        <div class="roster-hero-row">
          <span
            class="roster-hero"
            :title="roster.hero_name || t('tournament.roster_card.hero_not_specified')"
          >
            {{ roster.hero_name || t('tournament.roster_card.hero_not_specified') }}
          </span>
          <div class="roster-actions">
            <button
              @click="showRosterDetails"
              class="roster-btn roster-view-btn"
              type="button"
              :title="t('tournament.roster_card.show_roster')"
            >
              <i class="bi bi-card-list"></i>
            </button>
            <button
              v-if="roster.tournament_roster_url"
              @click="openRosterLink"
              class="roster-btn roster-link-btn"
              type="button"
              :title="t('tournament.roster_card.open_roster_new_tab')"
            >
              <i class="bi bi-box-arrow-up-right"></i>
            </button>
          </div>
        </div>
        <div class="roster-faction">{{ roster.faction_name }}</div>
      </div>
      <div v-if="isActive && !compact" class="active-indicator">
        <i class="bi bi-check-circle-fill"></i>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.roster-card {
  background-color: white;
  border: 2px solid #bd9c65;
  border-radius: 6px;
  padding: 6px;
  transition: all 0.2s ease;
  cursor: default;
  min-height: 60px;
  display: flex;
  flex-direction: column;
  gap: 4px;

  &.active {
    border-color: #4caf50;
    background-color: rgba(76, 175, 80, 0.1);
    box-shadow: 0 2px 4px rgba(76, 175, 80, 0.2);

    .roster-hero {
      color: #2e7d32;
      font-weight: bold;
    }

    .roster-faction {
      color: #2e7d32;
    }
  }

  &.clickable {
    cursor: pointer;

    &:hover {
      border-color: #2196f3;
      background-color: rgba(33, 150, 243, 0.05);
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(33, 150, 243, 0.2);
    }

    &:active {
      transform: translateY(0);
    }
  }

  &.inactive {
    opacity: 0.7;
    background-color: #f8f9fa;
    border-color: #dee2e6;
  }

  &.disabled {
    opacity: 0.5;
    background-color: #f0f0f0;
    border-color: #ccc;
    cursor: not-allowed;

    &:hover {
      transform: none;
      box-shadow: none;
    }

    .roster-actions {
      opacity: 0.7;
    }
  }

  &.compact {
    padding: 4px;
    min-height: 40px;
    border-width: 1px;
    border-radius: 4px;

    .roster-hero {
      font-size: 10px;
      max-width: 80px;
    }

    .roster-faction {
      font-size: 9px;
    }

    .roster-btn {
      padding: 2px;
      font-size: 8px;
      min-width: 18px;
      height: 18px;
      border-width: 1px;

      i {
        font-size: 7px;
      }
    }

    .roster-hero-row {
      gap: 4px;
      margin-bottom: 0;
    }
  }
}

.roster-header {
  display: flex;
  align-items: flex-start;
  gap: 4px;
}

.roster-index {
  background-color: #c29c58;
  color: #60451f;
  font-weight: bold;
  font-size: 10px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.roster-info {
  flex: 1;
  min-width: 0;
}

.roster-hero-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 1px;
}

.roster-hero {
  font-weight: bold;
  color: #60451f;
  font-size: 11px;
  line-height: 1.2;
  word-wrap: break-word;
  flex: 1;
  min-width: 0;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.roster-faction {
  color: #bd9c65;
  font-size: 10px;
  font-style: italic;
  line-height: 1.2;
}

.active-indicator {
  color: #4caf50;
  font-size: 14px;
  flex-shrink: 0;
}

.roster-actions {
  display: flex;
  gap: 3px;
  flex-shrink: 0;
}

.roster-btn {
  background: none;
  border: 1px solid #bd9c65;
  border-radius: 3px;
  padding: 4px;
  font-size: 9px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #60451f;
  min-width: 24px;
  height: 24px;

  &:hover {
    background-color: rgba(194, 156, 88, 0.1);
    border-color: #c29c58;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  i {
    font-size: 10px;
  }
}

.roster-link-btn {
  color: #2196f3;
  border-color: #2196f3;

  &:hover {
    background-color: rgba(33, 150, 243, 0.1);
    color: #1976d2;
  }
}

// Responsive design
@media (max-width: 768px) {
  .roster-card {
    padding: 4px;
    min-height: 50px;
  }

  .roster-hero-row {
    gap: 4px;
  }

  .roster-hero {
    font-size: 10px;
  }

  .roster-faction {
    font-size: 9px;
  }

  .roster-btn {
    padding: 2px;
    font-size: 8px;
    min-width: 18px;
    height: 18px;

    i {
      font-size: 7px;
    }
  }
}
</style>
